/*! custom-neo.js — LeGeekShop × Wayne Outfitters
   Optimisations Lighthouse : LCP/TBT/CLS, lazy images/iframes, 3P defer, lazy sections. */

(() => {
  "use strict";

  /* ------------------ Helpers robustes ------------------ */
  const on = (t, e, o, a = { passive: true }) => t.addEventListener(e, o, a);
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  const raf = (fn) => (window.requestAnimationFrame || setTimeout)(fn, 0);
  const idle = (fn, timeout = 1500) => {
    if ("requestIdleCallback" in window) return window.requestIdleCallback(fn, { timeout });
    return setTimeout(fn, timeout);
  };

  const whenInteractive = (fn) => {
    if (document.readyState === "interactive" || document.readyState === "complete") fn();
    else on(document, "DOMContentLoaded", fn, { passive: true });
  };

  const onVisible = (el, cb, rootMargin = "200px") => {
    if (!("IntersectionObserver" in window)) return cb();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          io.unobserve(e.target);
          cb(e.target);
        }
      });
    }, { rootMargin, threshold: 0.01 });
    io.observe(el);
  };

  /* ------------------ 1) Priorité LCP (image héros PDP) ------------------ */
  const boostLCP = () => {
    // Sélectionne la 1ère image “héros” visible de la PDP Prestige
    const lcpCandidates = [
      ".product-gallery img[loading]",     // déjà rendu par le thème
      ".Product__Gallery img",             // variantes de thèmes
      ".product__media img",
      ".gallery__image img",
      ".product-media img",
      ".media img"
    ];
    let lcp = null;
    for (const s of lcpCandidates) {
      lcp = $(s);
      if (lcp) break;
    }
    // Fallback : première image non décorative dans le viewport
    if (!lcp) {
      lcp = $$("img:not([aria-hidden='true']):not([role='presentation'])")
        .find((img) => img.getBoundingClientRect().top < window.innerHeight);
    }
    if (!lcp) return;

    // Donne la priorité réseau à l’image LCP
    try {
      lcp.setAttribute("fetchpriority", "high");
      // Aide à la stabilité (CLS) si width/height manquent
      if (!lcp.getAttribute("width") || !lcp.getAttribute("height")) {
        const r = lcp.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          lcp.setAttribute("width", Math.round(r.width));
          lcp.setAttribute("height", Math.round(r.height));
        }
      }
      // decoding async
      lcp.setAttribute("decoding", "async");
      // s’assurer qu’elle n’est pas lazy
      lcp.removeAttribute("loading");
    } catch (e) { /* noop */ }
  };

  /* ------------------ 2) Lazyload images non-critiques ------------------ */
  const lazyAllImages = () => {
    const imgs = $$("img");
    imgs.forEach((img, idx) => {
      // laisse la 1re image (LCP) telle quelle
      if (idx === 0) return;
      if (!img.closest("header")) {
        if (!img.hasAttribute("loading")) img.setAttribute("loading", "lazy");
        if (!img.hasAttribute("decoding")) img.setAttribute("decoding", "async");
      }
      // petits logos / icônes : reduce TBT avec decoding async
      if ((img.naturalWidth && img.naturalWidth <= 64) || /icon|logo/i.test(img.src)) {
        img.setAttribute("decoding", "async");
      }
    });
  };

  /* ------------------ 3) Lazyload iframes (YouTube, maps…) ------------------ */
  const lazyIframes = () => {
    $$("iframe").forEach((f) => {
      if (!f.hasAttribute("loading")) f.setAttribute("loading", "lazy");
      if (!f.hasAttribute("referrerpolicy")) f.setAttribute("referrerpolicy", "no-referrer-when-downgrade");
    });
  };

  /* ------------------ 4) Déférer scripts tiers via data-attrs ------------------
     Usage dans theme.liquid ou templates :
     <script data-3p="idle" data-src="https://example.com/sdk.js"></script>
     <script data-3p="visible" data-src="https://example.com/chat.js" data-target="#footer"></script>
     <script data-3p="delay" data-src="https://example.com/ads.js" data-delay="4000"></script>
  --------------------------------------------------------------------------- */
  const loadScript = (src, attrs = {}) =>
    new Promise((res, rej) => {
      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
      s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });

  const hydrate3P = () => {
    $$("script[data-3p]").forEach((stub) => {
      const mode = stub.getAttribute("data-3p");
      const src  = stub.getAttribute("data-src");
      if (!src) return;

      const doLoad = () => {
        loadScript(src, { async: "", crossorigin: stub.getAttribute("crossorigin") || "" })
          .catch(() => {/* ignore */});
      };

      switch (mode) {
        case "idle":
          idle(doLoad, parseInt(stub.getAttribute("data-timeout") || "1500", 10));
          break;
        case "delay":
          setTimeout(doLoad, parseInt(stub.getAttribute("data-delay") || "3000", 10));
          break;
        case "visible": {
          const targetSel = stub.getAttribute("data-target");
          const target = targetSel ? $(targetSel) : document.body;
          if (!target) { idle(doLoad); break; }
          onVisible(target, doLoad);
          break;
        }
        default: // fallback : idle
          idle(doLoad);
      }
    });
  };

  /* ------------------ 5) Lazy sections (reco, UGC, gros blocs) ------------------
     Marquer dans le HTML : <div data-lazy-section="#selectorToReveal">…</div>
     Le sélecteur pointant la section à afficher/charger (ex: recommendations).
  ------------------------------------------------------------------------------- */
  const initLazySections = () => {
    $$("[data-lazy-section]").forEach((holder) => {
      onVisible(holder, () => {
        const sel = holder.getAttribute("data-lazy-section");
        if (!sel) return;
        const target = $(sel) || holder;
        target.removeAttribute("hidden");
        target.classList.add("is-visible");
        // Option : déclencher un event pour init sliders/carrousels si besoin
        const ev = new CustomEvent("neo:lazy-section:show", { detail: { target }});
        document.dispatchEvent(ev);
      });
    });
  };

  /* ------------------ 6) Micro-optimisations TBT ------------------ */
  const sliceLongTasks = () => {
    // Décompose l’hydratation “légère” après l’interactive
    idle(() => {
      // Fonts preconnect (si Google Fonts est utilisé)
      try {
        const pre1 = document.createElement("link");
        pre1.rel = "preconnect"; pre1.href = "https://fonts.googleapis.com"; pre1.crossOrigin = "anonymous";
        const pre2 = document.createElement("link");
        pre2.rel = "preconnect"; pre2.href = "https://fonts.gstatic.com"; pre2.crossOrigin = "anonymous";
        document.head.appendChild(pre1); document.head.appendChild(pre2);
      } catch (e) {}
    }, 500);

    // Exemples d’inits non-critiques
    idle(() => {
      // Collapse/accordion natifs <details> : rien à faire, mais on peut ajouter un style/aria si besoin
      $$("details").forEach((d) => {
        if (!d.hasAttribute("data-inited")) {
          d.setAttribute("data-inited", "true");
          // amélioration accessibilité mineure
          const s = $("summary", d);
          if (s && !s.getAttribute("role")) s.setAttribute("role", "button");
        }
      });
    }, 1000);
  };

  /* ------------------ 7) Sélecteurs spécifiques Prestige (safe) ------------------ */
  const prestigeTweaks = () => {
    // Désactiver lazy sur la toute 1re image de la galerie si le thème l’a marquée lazy par défaut
    const firstGalleryImg = $(".product-gallery img, .product__media img, .media img");
    if (firstGalleryImg) firstGalleryImg.removeAttribute("loading");
  };

  /* ------------------ Orchestration ------------------ */
  whenInteractive(() => {
    try { boostLCP(); } catch (e) {}
    try { prestigeTweaks(); } catch (e) {}
    try { lazyAllImages(); } catch (e) {}
    try { lazyIframes(); } catch (e) {}
    try { initLazySections(); } catch (e) {}
    try { hydrate3P(); } catch (e) {}
    try { sliceLongTasks(); } catch (e) {}
  });

  // Sécurité : si l’image LCP switch après sélection variante, rebooster
  on(document, "change", (e) => {
    if (e.target && /variant|option/i.test(e.target.name || "")) {
      raf(() => boostLCP());
    }
  });

})();
